<template>
  <!--
    ArtifactDetail SECTION — DataAttribute artifact, "SQL" tab.

    WHAT: The SQL this DataAttribute runs, plus what currently composes it. The
      usage counts are the point: a DataAttribute is shared, so editing one
      changes every metric/worklist/flag/insight built on it (one edit to DA 567
      broke 53 metrics across two tenants).

    CONTRACT (props { record, artifact, context }):
      READS  record (list row: sql via detail fetch, usage counts from the list)
      EMITS  refresh — after the editor drawer saves, so the shell reloads.

    NOTES:
      - Editing REUSES PrimitiveEditorDrawer, the same component the join builder
        opens. Do not reimplement DA editing here; the drawer owns validation and
        the server's sql_query error surfacing.
      - selfChrome: draws its own card.
  -->
  <div>
    <div class="bg-white rounded-lg border border-gray-200 mb-3">
      <div class="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
        <div class="min-w-0">
          <div class="text-xs font-semibold text-gray-700">SQL</div>
          <div class="text-[11px] text-gray-400 mt-0.5 truncate">
            Placeholders like <code class="font-mono">{tenant_id}</code> are substituted by the engine at run time.
          </div>
        </div>
        <button
          class="text-[11px] font-semibold text-white rounded px-2.5 py-1.5 hover:opacity-90 whitespace-nowrap"
          :style="{ background: artifact.accent || '#0891B2' }"
          @click="editing = true"
        >
          Edit SQL
        </button>
      </div>
      <pre class="text-[10px] font-mono p-3 overflow-auto max-h-[28rem] whitespace-pre-wrap text-gray-800">{{ record.sql_query || '— no SQL —' }}</pre>
    </div>

    <div class="bg-white rounded-lg border border-gray-200">
      <div class="px-4 py-3 border-b border-gray-100">
        <div class="text-xs font-semibold text-gray-700">Used by</div>
        <div class="text-[11px] text-gray-400 mt-0.5">
          Everything composing this attribute. All of it changes when you edit the SQL.
        </div>
      </div>
      <div class="px-4 py-3 grid grid-cols-5 gap-3">
        <div v-for="u in usage" :key="u.label">
          <div class="text-lg font-semibold" :class="u.n ? 'text-gray-900' : 'text-gray-300'">{{ u.n }}</div>
          <div class="text-[11px] text-gray-500">{{ u.label }}</div>
        </div>
      </div>
      <div v-if="isOrphan" class="px-4 pb-3">
        <div class="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
          Nothing references this attribute. It is safe to change, and a candidate
          for deactivation.
        </div>
      </div>
    </div>

    <PrimitiveEditorDrawer
      v-if="editing"
      :attribute-id="record.id"
      @close="editing = false"
      @saved="onSaved"
    />
  </div>
</template>

<script>
import PrimitiveEditorDrawer from '@/components/Admin/MetricStudio/drawers/PrimitiveEditorDrawer.vue';

export default {
  name: 'AttributeSqlSection',
  components: { PrimitiveEditorDrawer },
  props: {
    record: { type: Object, required: true },
    artifact: { type: Object, required: true },
    context: { type: Object, default: () => ({}) },
  },
  emits: ['refresh'],
  data() {
    return { editing: false };
  },
  computed: {
    usage() {
      const r = this.record;
      return [
        { label: 'Metrics', n: r.used_by_metrics ?? 0 },
        { label: 'Worklists', n: r.used_by_worklists ?? 0 },
        { label: 'Flags', n: r.used_by_flags ?? 0 },
        { label: 'Insights', n: r.used_by_insights ?? 0 },
        // Opportunity primitives bind via opportunity_config, not studio steps.
        { label: 'Opportunities', n: r.used_by_opportunities ?? 0 },
      ];
    },
    isOrphan() {
      // Only claim "unused" when the counts were actually supplied; the detail
      // endpoint does not carry them, so absent !== zero.
      const r = this.record;
      const known = ['used_by_metrics', 'used_by_worklists', 'used_by_flags',
        'used_by_insights', 'used_by_opportunities']
        .some((k) => r[k] !== undefined && r[k] !== null);
      return known && this.usage.every((u) => !u.n);
    },
  },
  methods: {
    onSaved() {
      this.editing = false;
      this.$emit('refresh');
    },
  },
};
</script>
